import React from 'react';
import { useSelector } from 'react-redux';
import { creditAppSelectors } from '../store/config';
import { CheckmarkIcon } from './CheckmarkIcon';
import {
    StyledCreditAppSummary,
    SummaryCard,
    SummaryHeader,
    SummaryBody,
    SummaryRow,
    Label,
    Value,
    SuccessIcon
} from './CreditAppSummary.styles';

/**
 * Credit App Summary Component
 *
 * Displays a custom summary page when credit app is completed
 * Shows creditAppId, dealRefId, and shopper information
 * Replaces the iframe when user navigates back to credit app after completion
 */
const CreditAppSummary: React.FC = () => {
    const creditAppId = useSelector(creditAppSelectors.getCreditAppId);
    const dealRefId = useSelector(creditAppSelectors.getDealRefId);
    const shopper = useSelector(creditAppSelectors.getShopper);

    const shopperName = shopper?.name || `${shopper?.firstName || ''} ${shopper?.lastName || ''}`.trim();

    // Define summary rows data
    const summaryRows = [
        {
            key: 'credit-app-id',
            label: 'Credit Application ID:',
            value: creditAppId,
            testId: 'credit-app-id-row'
        },
        {
            key: 'deal-ref-id',
            label: 'Deal Reference ID:',
            value: dealRefId,
            testId: 'deal-ref-id-row'
        },
        {
            key: 'shopper-name',
            label: 'Applicant Name:',
            value: shopperName,
            testId: 'shopper-name-row'
        },
        ...(shopper?.email
            ? [
                  {
                      key: 'shopper-email',
                      label: 'Email:',
                      value: shopper.email,
                      testId: 'shopper-email-row'
                  }
              ]
            : []),
        {
            key: 'status',
            label: 'Status:',
            value: 'Submitted',
            testId: 'status-row',
            style: { color: '#21b500', fontWeight: 600 }
        }
    ];

    return (
        <StyledCreditAppSummary data-testid="credit-app-summary">
            <SummaryCard>
                <SummaryHeader>
                    <SuccessIcon data-testid="success-icon">
                        <CheckmarkIcon />
                    </SuccessIcon>
                    <h1>Credit Application Submitted Successfully</h1>
                    <p>Your credit application has been submitted and is being processed.</p>
                </SummaryHeader>

                <SummaryBody>
                    <h2>Application Details</h2>

                    {summaryRows.map((row) => (
                        <SummaryRow key={row.key} data-testid={row.testId}>
                            <Label>{row.label}</Label>
                            <Value style={row.style}>{row.value}</Value>
                        </SummaryRow>
                    ))}
                </SummaryBody>

                <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>
                        <strong>Note:</strong> You will be notified once your credit application has been reviewed. This process
                        typically takes a few minutes to complete.
                    </p>
                </div>
            </SummaryCard>
        </StyledCreditAppSummary>
    );
};

export default CreditAppSummary;
